export type SupportedCurrency = 'DKK' | 'EUR' | 'USD';

function getStoredAppLanguage(): 'da' | 'en' {
  try {
    const stored = localStorage.getItem('i18nextLng');
    if (stored?.toLowerCase().startsWith('da')) return 'da';
    if (stored?.toLowerCase().startsWith('en')) return 'en';
  } catch {
    // ignore
  }
  return 'en';
}

/**
 * When the UI language is Danish, always use DKK (real Danish prices).
 * Otherwise honor preferred_currency, then browser locale.
 */
export const detectUserCurrency = (): SupportedCurrency => {
  if (getStoredAppLanguage() === 'da') return 'DKK';

  const saved = localStorage.getItem('preferred_currency');
  if (saved && ['DKK', 'EUR', 'USD'].includes(saved)) {
    return saved as SupportedCurrency;
  }

  const locale = navigator.language;

  if (locale.startsWith('da') || locale.includes('DK')) return 'DKK';
  if (locale.includes('US') || locale.includes('CA')) return 'USD';
  if (locale.startsWith('en')) return 'USD';

  const europeanLocales = ['de', 'fr', 'es', 'it', 'nl', 'pl', 'se', 'no', 'fi'];
  if (europeanLocales.some(lang => locale.startsWith(lang))) return 'EUR';

  return 'EUR';
};

export const isCurrencyLockedToDkk = (): boolean => getStoredAppLanguage() === 'da';

export const saveCurrencyPreference = (currency: SupportedCurrency) => {
  // Danish UI always shows DKK — still store preference for when user switches to EN
  localStorage.setItem('preferred_currency', currency);
};

export const formatCurrency = (amount: number, currency: SupportedCurrency): string => {
  const locale = currency === 'DKK' ? 'da-DK' : currency === 'EUR' ? 'de-DE' : 'en-US';
  const shouldShowDecimals = currency !== 'DKK' || !Number.isInteger(amount);
  const fractionDigits = shouldShowDecimals ? 2 : 0;

  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

  return formatter.format(amount);
};
